"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { motion, AnimatePresence, Variants } from "framer-motion";
import appendImageUrlFromAPI from "@/utils/appendImageUrlFromAPI";
import useWindowSize from "@/hooks/useWindowSize";
import ActionButton from "@/components/Common/ActionButton";
import { Title, Description } from "@/components/Common/Typography";
import { useTranslatedText } from "@/hooks/useProductTranslation";
import styles from "./productSpotlightSection.module.css";

const AUTO_TIME = 7000;

function TranslatedSpan({ text, locale }: { text: string; locale: string }) {
  const display = useTranslatedText(text, locale);
  return <>{display}</>;
}

const getLocalizedProductLink = (url: string, locale: string) => {
  if (!url) return `/${locale}/san-pham`;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  const clean = url.startsWith("/") ? url : `/${url}`;
  if (clean.startsWith(`/${locale}/`) || clean === `/${locale}`) return clean;
  return `/${locale}${clean}`;
};

interface ProductSpotlightSectionProps {
  products?: any[];
  data?: any;
  locale?: string;
}

export default function ProductSpotlightSection({
  products: propProducts,
  data,
  locale = "vi",
}: ProductSpotlightSectionProps) {
  const products = useMemo(() => {
    if (propProducts && propProducts.length > 0) {
      return propProducts;
    }
    return (data?.add_on_data_3 || []).map((item: any) => ({
      id: item.id || item.stt,
      title: item.title,
      description: item.content?.replace(/<[^>]*>/g, ""),
      link: item.summary,
      color: item.content_2 || item.content_3 || "#008748",
      mainImage1: item.picture,
      mainImage2: item.picture_2,
      vec: item.picture_3,
    }));
  }, [propProducts, data]);

  const [current, setCurrent] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [hovered, setHovered] = useState<number | null>(null);
  const { isDesktop, isTablet, isMobile } = useWindowSize();

  useEffect(() => {
    if (!products.length) return;

    const startAutoplay = () => {
      intervalRef.current = setInterval(() => {
        setCurrent((prev) => (prev + 1) % products.length);
      }, AUTO_TIME);
    };

    startAutoplay();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [products]);

  const resetAutoplay = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % products.length);
    }, AUTO_TIME);
  };

  const handleSelect = (index: number) => {
    setCurrent(index);
    resetAutoplay();
  };

  if (products.length === 0) return null;

  const product = products[current];
  const isEven = current % 2 === 1;

  return (
    <section
      className={styles.section}
      style={{
        minHeight: isDesktop || isTablet ? "100vh" : "auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: isDesktop || isTablet ? "center" : "flex-start",
        paddingTop: isDesktop
          ? "clamp(100px, 12vh, 160px)"
          : isTablet
            ? "clamp(40px, 5vh, 65px)"
            : "44px",
        paddingBottom: isDesktop
          ? "clamp(60px, 8vh, 100px)"
          : isTablet
            ? "clamp(30px, 4vh, 50px)"
            : "10px",
      }}
    >
      {isMobile ? (
        <MobileLayout product={product} locale={locale} />
      ) : (
        <DesktopTabletLayout
          product={product}
          isEven={isEven}
          isDesktop={isDesktop}
          isTablet={isTablet}
          locale={locale}
        />
      )}

      {/* Navigation buttons */}
      <div
        className={styles.navContainer}
        style={{
          position: isDesktop || isTablet ? "absolute" : "relative",
          bottom: isDesktop ? "6vh" : isTablet ? "3vh" : undefined,
          marginTop: isDesktop || isTablet ? undefined : "24px",
          marginBottom: isDesktop || isTablet ? undefined : "20px",
          left: isDesktop || isTablet ? "50%" : undefined,
          transform: isDesktop || isTablet ? "translateX(-50%)" : undefined,
          zIndex: 30,
        }}
      >
        {products.map((p: any, index: any) => {
          const isActive = index === current;
          const isButtonHovered = hovered === index;

          return (
            <motion.button
              key={p.id}
              onClick={() => handleSelect(index)}
              onMouseEnter={() => setHovered(index)}
              onMouseLeave={() => setHovered(null)}
              whileTap={{ scale: 0.94 }}
              className={styles.navButton}
              style={{
                fontSize: isDesktop
                  ? "clamp(14px, 1vw, 18px)"
                  : isTablet
                    ? "clamp(8px, 1.6vw, 12px)"
                    : "clamp(10px, 3.6vw, 12px)",
                backgroundColor:
                  isButtonHovered || isActive ? p.color : "#003E2D",
                boxShadow:
                  isButtonHovered || isActive
                    ? `0 8px 20px ${p.color}50`
                    : "0 3px 10px rgba(0,0,0,0.25)",
                transform:
                  isButtonHovered || isActive
                    ? "translateY(-2px)"
                    : "translateY(0)",
                opacity: isActive ? 1 : 0.9,
              }}
            >
              <TranslatedSpan text={p.title} locale={locale} />
            </motion.button>
          );
        })}
      </div>
      {/* Hidden preloading images */}
      <div style={{ display: "none" }}>
        {products.map((p: any) => (
          <div key={`preload-${p.id}`}>
            <Image
              src={appendImageUrlFromAPI({ src: p.mainImage1, size: "2k" })}
              alt="preload"
              width={10}
              height={10}
            />
            <Image
              src={appendImageUrlFromAPI({ src: p.mainImage2, size: "2k" })}
              alt="preload"
              width={10}
              height={10}
            />
            {p.vec && (
              <Image
                src={appendImageUrlFromAPI({ src: p.vec })}
                alt="preload"
                width={10}
                height={10}
              />
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function MobileLayout({
  product,
  locale,
}: {
  product: any;
  locale: string;
}) {
  return (
    <div
      className={styles.mobileLayout}
      style={{ position: "relative", zIndex: 10 }}
    >
      <Title size="large" color={product.color}>
        <TranslatedSpan text={product.title} locale={locale} />
      </Title>

      <Description size="medium" style={{ marginBottom: "15px" }}>
        <TranslatedSpan text={product.description} locale={locale} />
      </Description>

      <div style={{ marginBottom: "15px" }}>
        <ActionButton
          label={locale === "vi" ? "Khám phá ngay" : "Explore now"}
          color={product.color}
          hoverColor="#FFCD00"
          link={getLocalizedProductLink(product.link, locale)}
          style={{
            padding: "10px 26px 10px 32px",
            fontWeight: 600,
            fontSize: "15px",
          }}
        />
      </div>

      <div
        className="relative mx-auto"
        style={{
          width: "100%",
          height: "32vh",
          minHeight: "220px",
          maxHeight: "320px",
          marginTop: "10px",
          position: "relative",
          zIndex: 5,
        }}
      >
        <div
          style={{
            position: "relative",
            width: "100%",
            height: "100%",
          }}
        >
          <Image
            src={appendImageUrlFromAPI({ src: product.mainImage1, size: "2k" })}
            alt={product.title}
            fill
            style={{ objectFit: "contain" }}
            priority
            sizes="100vw"
          />
        </div>
      </div>

      {product.vec && (
        <div
          style={{
            position: "absolute",
            bottom: "0%",
            left: "50%",
            transform: "translateX(-50%)",
            width: 320,
            height: 180,
            opacity: 0.7,
            pointerEvents: "none",
            zIndex: 1,
          }}
        >
          <Image
            src={appendImageUrlFromAPI({ src: product.vec })}
            alt="decoration"
            fill
            style={{ objectFit: "contain" }}
          />
        </div>
      )}
    </div>
  );
}

function DesktopTabletLayout({
  product,
  isEven,
  isDesktop,
  isTablet,
  locale,
}: {
  product: any;
  isEven: boolean;
  isDesktop: boolean;
  isTablet: boolean;
  locale: string;
}) {
  const circleVariants: Variants = {
    hidden: { opacity: 0, scale: 0.6 },
    visible: (custom: any) => {
      const { i, isDesktop, isTablet } = custom;
      const angles = [40, 110, 200];
      const angle = (angles[i] || 0) * (Math.PI / 180);
      const vw = typeof window !== "undefined" ? window.innerWidth : 1920;

      // Giảm khoảng cách bán kính bay của circle tới tâm
      const base = isDesktop
        ? Math.min(Math.max(vw * 0.16, 100), 200)
        : isTablet
          ? Math.min(Math.max(vw * 0.14, 120), 200)
          : Math.min(Math.max(vw * 0.16, 80), 140);

      return {
        opacity: 1,
        scale: 1,
        x: [0, Math.cos(angle) * base],
        y: [0, -Math.sin(angle) * base],
        transition: {
          delay: i * 0.12,
          duration: 1.2,
          type: "spring",
          stiffness: 110,
          damping: 14,
        },
      };
    },
  };

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={product.id}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.6 }}
        className={styles.container}
      >
        <div
          className={styles.grid}
          style={{
            flexDirection: isEven ? "row-reverse" : "row",
          }}
        >
          {/* Hình phụ */}
          <div className={styles.col3}>
            <div
              style={{
                display: "flex",
                justifyContent: isEven ? "flex-end" : "flex-start",
                overflow: "hidden",
                marginLeft: isEven ? 0 : "-10px",
                marginRight: isEven ? "-10px" : 0,
              }}
            >
              <motion.div
                initial={{ x: isEven ? 250 : -250, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 1 }}
                style={{
                  position: "relative",
                  width: "150%",
                  height: isDesktop ? "50vh" : isTablet ? "36vh" : "30vh",
                  transform: isEven ? "translateX(20%)" : "translateX(-20%)",
                }}
              >
                <Image
                  src={appendImageUrlFromAPI({
                    src: product.mainImage2,
                    size: "2k",
                  })}
                  alt={product.title}
                  fill
                  style={{
                    objectFit: "contain",
                    objectPosition: isEven ? "right center" : "left center",
                    maxWidth: "none",
                  }}
                  priority
                  sizes="(max-width: 1200px) 50vw, 33vw"
                />
              </motion.div>
            </div>
          </div>

          {/* Nội dung */}
          <div
            className={styles.col3}
            style={{ padding: isDesktop ? "0 40px" : "0 16px" }}
          >
            <div>
              <Title size="large" color={product.color} align="left">
                <TranslatedSpan text={product.title} locale={locale} />
              </Title>

              <Description size="medium" style={{ marginTop: "14px" }}>
                <TranslatedSpan text={product.description} locale={locale} />
              </Description>

              <div style={{ marginTop: "14px" }}>
                <ActionButton
                  label={locale === "vi" ? "Khám phá ngay" : "Explore now"}
                  color={product.color}
                  hoverColor="#FFCD00"
                  link={getLocalizedProductLink(product.link, locale)}
                  style={{
                    padding: isDesktop
                      ? "10px 26px 10px 32px"
                      : isTablet
                        ? "5px 26px 5px 32px"
                        : "5px 12px",
                    fontWeight: isDesktop ? 600 : isTablet ? 500 : 400,
                    fontSize: isDesktop ? "15px" : isTablet ? "13px" : "12px",
                  }}
                />
              </div>
            </div>
          </div>

          {/* Hình chính + Deco */}
          <div className={styles.col6} style={{ position: "relative" }}>
            <div
              className={styles.mainImageWrapper}
              style={{
                justifyContent: isEven ? "flex-start" : "flex-end",
              }}
            >
              <motion.div
                className={styles.mainImage}
                style={{
                  position: "relative",
                  width: "100%",
                  height: isDesktop
                    ? "clamp(260px, 32vw, 48vh)"
                    : isTablet
                      ? "clamp(200px, 28vw, 36vh)"
                      : "clamp(180px, 25vw, 30vh)",
                  marginRight: isEven ? "0" : "auto",
                  marginLeft: isEven ? "auto" : "0",
                }}
                initial={{ opacity: 0.6, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.9 }}
              >
                <div
                  style={{
                    position: "absolute",
                    height: "90%",
                    width: isDesktop ? "75%" : isTablet ? "85%" : "70%",
                    left: isEven ? "auto" : isTablet ? "5%" : "10%",
                    right: isEven ? (isTablet ? "5%" : "10%") : "auto",
                    top: isDesktop ? "22%" : isTablet ? "14%" : "12%",
                  }}
                >
                  <Image
                    src={appendImageUrlFromAPI({
                      src: product.mainImage1,
                      size: "2k",
                    })}
                    alt={product.title}
                    fill
                    style={{
                      objectFit: "contain",
                    }}
                    priority
                    sizes="(max-width: 1200px) 70vw, 50vw"
                  />
                </div>
              </motion.div>

              {/* Circle Deco */}
              {[0, 1, 2].map((i) => {
                const size = getCircleSize(i, isDesktop, isTablet);
                return (
                  <motion.div
                    key={i}
                    custom={{ i, isDesktop, isTablet }}
                    variants={circleVariants}
                    initial="hidden"
                    animate="visible"
                    className={styles.circleDeco}
                    style={{
                      left: isEven ? "50%" : "35%",
                      top: isTablet ? "40%" : "45%",
                      width: size,
                      height: size,
                      background:
                        i === 0 ? `${product.color}cc` : `${product.color}aa`,
                      marginRight: isEven ? (isDesktop ? 60 : 20) : "auto",
                      marginLeft: isEven ? "auto" : isDesktop ? 60 : 20,
                    }}
                  />
                );
              })}

              {/* Vector */}
              {product.vec && (
                <div
                  className={styles.vectorDeco}
                  style={{
                    top: isDesktop ? "72%" : isTablet ? "68%" : "75%",
                    left: isEven ? "50%" : "35%",
                    marginRight: isEven ? (isDesktop ? 60 : 20) : "auto",
                    marginLeft: isEven ? "auto" : isDesktop ? 60 : 20,
                    width: getDecorVecSize(isDesktop, isTablet),
                    height: isTablet ? "24vh" : "32vh",
                    opacity: 0.85,
                    zIndex: 2,
                    transform: "translate(-50%, 0)",
                  }}
                >
                  <Image
                    src={appendImageUrlFromAPI({ src: product.vec })}
                    alt="decoration vector"
                    fill
                    style={{ objectFit: "contain" }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

const getCircleSize = (i: number, isDesktop: boolean, isTablet: boolean) => {
  if (isDesktop) {
    return `clamp(${40 - i * 10}px, ${8 - i * 1.8}vw, ${90 - i * 25}px)`;
  }
  if (isTablet) {
    return `clamp(${35 - i * 6}px, ${8 - i * 1.5}vw, ${80 - i * 15}px)`;
  }
  return `clamp(${28 - i * 5}px, ${10 - i * 2}vw, ${55 - i * 10}px)`;
};

const getDecorVecSize = (isDesktop: boolean, isTablet: boolean) => {
  if (isDesktop) {
    return "clamp(200px, 25vw, 450px)";
  }
  if (isTablet) {
    return "clamp(160px, 25vw, 350px)";
  }
  return "clamp(140px, 30vw, 250px)";
};
